import { useQuery } from 'react-query'
import { queryClient } from '../queries/utils/queryClient'
import { EXPLORER_URL } from '../constants'

interface ExplorerBlockResponse {
  message: string
  result: {
    blockNumber: string
  }
  status: string
}

/**
 * Fetches block number for a given timestamp using the explorer API
 * Uses non-reactive fetch with permanent caching
 */
async function fetchBlockByTimestamp(timestamp: number): Promise<number | null> {
  if (!EXPLORER_URL) {
    throw new Error('EXPLORER_URL is not configured')
  }

  const queryKey = ['block', timestamp]

  // Try to get from cache first
  const cachedData = queryClient.getQueryData<number>(queryKey)
  if (cachedData !== undefined) {
    return cachedData
  }

  // If not in cache, fetch from API
  try {
    const data = await queryClient.fetchQuery(
      queryKey,
      async () => {
        const url = `${EXPLORER_URL}/api?module=block&action=getblocknobytime&timestamp=${timestamp}&closest=before`

        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const apiData: ExplorerBlockResponse = await response.json()

        if (apiData.status === '1' && apiData.result?.blockNumber) {
          return parseInt(apiData.result.blockNumber, 10)
        }

        throw new Error('Invalid response from explorer API')
      },
      {
        staleTime: Infinity, // Never consider stale - blocks don't change
        cacheTime: Infinity, // Never remove from cache
      }
    )

    return data
  } catch (error) {
    console.error('Error fetching block by timestamp:', error)
    throw error
  }
}

/**
 * @notice Fetches block objects for an array of timestamps.
 * @dev blocks are returned in the same order as input timestamps
 * @dev blocks are returned as numbers
 * @dev timestamps are returns as they were provided; not the block time.
 * @param {Array} timestamps
 */
async function getBlocksFromTimestamps(timestamps: number[]): Promise<{ timestamp: string; number: number }[]> {
  if (timestamps.length === 0) {
    return []
  }

  const promises = timestamps.map(async (timestamp) => {
    try {
      const blockNumber = await fetchBlockByTimestamp(timestamp)
      if (blockNumber !== null) {
        return {
          timestamp: timestamp.toString(),
          number: blockNumber,
        }
      }
      return {
        timestamp: timestamp.toString(),
        number: 0, // Return 0 for failed requests to maintain order
      }
    } catch (error) {
      console.error(`Error fetching block for timestamp ${timestamp}:`, error)
      return {
        timestamp: timestamp.toString(),
        number: 0, // Return 0 for failed requests to maintain order
      }
    }
  })

  const results = await Promise.all(promises)
  return results
}

/**
 * for a given array of timestamps, returns block entities
 * @param timestamps
 */
export function useBlocksFromTimestamps(
  timestamps: number[]
): {
  blocks:
    | {
        timestamp: string
        number: any
      }[]
    | undefined
  error: boolean
} {
  // Create a cache key from timestamps in original order
  const cacheKey = timestamps.join(',')

  const { data: blocks, error } = useQuery(['blocks', cacheKey], () => getBlocksFromTimestamps(timestamps), {
    staleTime: 1000 * 60 * 60, // 1 hour - blocks don't change
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: 3,
    enabled: timestamps.length > 0,
  })

  return {
    blocks,
    error: !!error,
  }
}
